import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChalkboardTeacher, FaUnlink } from "react-icons/fa";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import {
  unlinkTeacherUser,
  updateTeacher,
  updateTeacherContactSharing,
} from "../api";
import { loadTeacherDetails, upsertCachedTeacher } from "../cache";
import {
  ActionButton,
  BackButton,
  LdsPage,
  StatusBadge,
} from "../components/Lds";
import type { AppError, Teacher, TeacherFormValues } from "../types";
import {
  httpStatus,
  teacherInitials,
  teacherName,
  toDateInput,
} from "../utils";
import TeacherForm from "./TeacherForm";

import { useLeaveForm } from "../../../components/navigation/BackButton";
import { useFormDirtyState, useUnsavedChangesGuard } from "../../../components/navigation/UnsavedChangesGuard";
import { withComparableAddress } from "../../../services/dutchAddressLookup";
const valuesFromTeacher = (teacher: Teacher): TeacherFormValues => ({
  firstName: teacher.firstName ?? "",
  lastName: teacher.lastName ?? "",
  address: teacher.address ?? "",
  zipCode: teacher.zipCode ?? "",
  city: teacher.city ?? "",
  phone: teacher.phone ?? "",
  email: teacher.email ?? "",
  dateBirth: toDateInput(teacher.dateBirth),
  sharePhone: Boolean(teacher.sharePhone),
  shareEmail: Boolean(teacher.shareEmail),
  shareAddress: Boolean(teacher.shareAddress),
  capabilities: teacher.capabilities?.length ? teacher.capabilities : ["CLASS_TEACHER"],
  subjectIds: teacher.subjects?.map((subject) => String(subject.id)) ?? [],
});

/** The form values with the address lookup's formatting ignored. */
const dirtyCheckValues = (values: TeacherFormValues | null) => (values ? withComparableAddress(values) : null);

const TeacherEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const { t } = useTranslation();

  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [values, setValues] = useState<TeacherFormValues | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const { isDirty, markClean } = useFormDirtyState(dirtyCheckValues(values));
  const { leaveWithoutWarning } = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!id) {
        setError({
          message: t("error.403.message", {
            defaultValue: "You do not have permission.",
          }),
          status: 403,
        });
        setLoading(false);
        return;
      }

      try {
        const data = await loadTeacherDetails(id);
        if (!cancelled) {
          const loadedValues = valuesFromTeacher(data);
          setTeacher(data);
          setValues(loadedValues);
          markClean(dirtyCheckValues(loadedValues));
        }
      } catch (err) {
        if (!cancelled) {
          setError({
            message:
              httpStatus(err) === 404
                ? t("teachers.details.not_found", {
                    defaultValue: "Teacher not found.",
                  })
                : httpStatus(err) === 403
                  ? t("error.403.message", {
                      defaultValue: "You do not have permission.",
                    })
                  : t("teachers.edit.load_failed", {
                      defaultValue: "Failed to fetch teacher details.",
                    }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [id, markClean, t]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id || !values) return;

    setSubmitting(true);
    setError(null);

    try {
      const updated = await updateTeacher(id, values);
      const sharing = await updateTeacherContactSharing(id, values);
      const next = {
        ...teacher,
        ...updated,
        ...(sharing ?? {}),
        ...values,
        id: updated.id ?? id,
      };
      upsertCachedTeacher(next);
      markClean(dirtyCheckValues(values));
      leaveWithoutWarning(() => leaveForm(`/school/teachers/${id}/details`));
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 403
            ? t("error.403.message", {
                defaultValue: "You do not have permission.",
              })
            : t("teachers.edit.update_failed", {
                defaultValue: "Failed to update teacher.",
              }),
        status: httpStatus(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlink = async () => {
    if (!id || !teacher) return;

    setUnlinkBusy(true);
    try {
      await unlinkTeacherUser(id);
      const next = { ...teacher, keycloakUserId: null };
      setTeacher(next);
      upsertCachedTeacher(next);
      setShowUnlinkConfirm(false);
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 403
            ? t("error.403.message", {
                defaultValue: "You do not have permission.",
              })
            : t("teachers.edit.unlink_failed", {
                defaultValue: "Failed to unlink Keycloak account.",
              }),
        status: httpStatus(err),
      });
    } finally {
      setUnlinkBusy(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => setError(null)}
      />
    );
  }
  if (!teacher || !values) return null;

  const linked = Boolean(teacher.keycloakUserId);

  return (
      <LdsPage>
        <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback={`/school/teachers/${id}/details`} />

          {linked ? (
            <ActionButton
              tone="danger"
              onClick={() => setShowUnlinkConfirm(true)}
            >
              <span className="inline-flex items-center gap-2">
                <FaUnlink aria-hidden="true" />
                {t("teachers.edit.unlink_button", {
                  defaultValue: "Unlink account",
                })}
              </span>
            </ActionButton>
          ) : null}
        </div>

        <section className="relative overflow-hidden rounded-[28px] border border-violet-100 bg-gradient-to-r from-violet-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-violet-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[30%]">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-violet-50 text-xl font-bold text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-violet-950 dark:text-violet-100 sm:h-24 sm:w-24">
              {teacherInitials(teacher)}
            </div>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("teachers.edit.title", { defaultValue: "Edit teacher" })}
              </p>
              <h1 className="mt-1 truncate text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                {teacherName(teacher)}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                {t("teachers.edit.subtitle", {
                  defaultValue:
                    "Update personal details, contact information and publication settings.",
                })}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge tone={linked ? "success" : "warn"}>
                  {linked
                    ? t("teachers.edit.linked_badge", {
                        defaultValue: "Linked",
                      })
                    : t("teachers.edit.not_linked_badge", {
                        defaultValue: "Not linked",
                      })}
                </StatusBadge>
                <StatusBadge tone="info">
                  <span className="inline-flex items-center gap-1.5">
                    <FaChalkboardTeacher aria-hidden="true" />
                    {t("teachers.details.teacher_role", {
                      defaultValue: "Teacher",
                    })}
                  </span>
                </StatusBadge>
              </div>
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaChalkboardTeacher className="text-[110px] text-violet-200/70 dark:text-violet-800/40" />
          </div>
        </section>

        <TeacherForm
          values={values}
          onChange={setValues}
          onSubmit={handleSubmit}
          onCancel={() => leaveWithoutWarning(() => navigate(`/school/teachers/${id}/details`))}
          submitting={submitting}
          submitLabel={t("common.save", { defaultValue: "Save" })}
          allowSharing
        />
      </div>

      <Modal
        show={showUnlinkConfirm}
        type="confirm"
        isConfirm
        title={t("common.modal.are_you_sure", {
          defaultValue: "Are you sure?",
        })}
        message={t("teachers.edit.unlink_confirm_message", {
          defaultValue:
            "This removes the link to the account. The teacher must link the account again before signing in as this teacher.",
        })}
        onConfirm={handleUnlink}
        onClose={() => !unlinkBusy && setShowUnlinkConfirm(false)}
        confirmLabel={
          unlinkBusy
            ? t("common.working", { defaultValue: "Working..." })
            : t("teachers.edit.unlink_button", {
                defaultValue: "Unlink account",
              })
        }
        disableActions={unlinkBusy}
      />
    </LdsPage>
  );
};

export default TeacherEdit;
